import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import CustomButton from "components/common/CustomButton";
import FileSearch from "components/fileStorage/FileSearch";
import {
  useGetConnectionTypesQuery,
  useGetRunStatusesQuery,
} from "features/integrationsBeta/platformEnums";
import type { TimezoneDisplayMode } from "features/integrationsBeta/timezoneDisplay";
import ClientFilterAutocomplete from "./ClientFilterAutocomplete";
import { FILTER_FIELD_H, FILTER_FIELD_W } from "./DateRangeSelect";
import FilterSelect from "./FilterSelect";
import { RunDatePairFilter, TypePairFilter } from "./PairedFilters";
import TimezoneDisplayToggle from "./TimezoneDisplayToggle";

export interface IntegrationsFilters {
  last_run_after?: string;
  last_run_before?: string;
  next_run_after?: string;
  next_run_before?: string;
  client?: string;
  source_type?: string;
  target_type?: string;
  last_status?: string;
}

interface Props {
  filters: IntegrationsFilters;
  onChange: (filters: IntegrationsFilters) => void;
  search: string;
  onSearch: (term: string | null) => void;
  timezoneMode: TimezoneDisplayMode;
  onTimezoneModeChange: (mode: TimezoneDisplayMode) => void;
  showRegister?: boolean;
  onRegister?: () => void;
}

export default function IntegrationsFilterBar({
  filters,
  onChange,
  search,
  onSearch,
  timezoneMode,
  onTimezoneModeChange,
  showRegister,
  onRegister,
}: Props) {
  const { data: connectionTypes = [] } = useGetConnectionTypesQuery();
  const { data: runStatuses = [] } = useGetRunStatusesQuery();

  const set = (patch: Partial<IntegrationsFilters>) =>
    onChange({ ...filters, ...patch });

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <Box sx={{ width: FILTER_FIELD_W }}>
        <FileSearch
          onSearch={onSearch}
          label="Search"
          size="small"
          initialValue={search}
          fullWidth
        />
      </Box>

      <ClientFilterAutocomplete
        value={filters.client}
        onChange={(client) => set({ client })}
      />
      <TypePairFilter
        options={connectionTypes}
        source={filters.source_type}
        target={filters.target_type}
        onChange={({ source, target }) =>
          set({ source_type: source, target_type: target })
        }
      />
      <FilterSelect
        label="Last Status"
        value={filters.last_status ?? ""}
        options={runStatuses}
        onChange={(last_status) =>
          set({ last_status: last_status || undefined })
        }
      />
      <RunDatePairFilter
        lastRun={{
          after: filters.last_run_after,
          before: filters.last_run_before,
        }}
        nextRun={{
          after: filters.next_run_after,
          before: filters.next_run_before,
        }}
        onChange={({ lastRun, nextRun }) =>
          set({
            last_run_after: lastRun.after,
            last_run_before: lastRun.before,
            next_run_after: nextRun.after,
            next_run_before: nextRun.before,
          })
        }
      />

      <CustomButton fullWidth={false} onClick={() => onChange({})}>
        Clear Filters
      </CustomButton>

      <Box sx={{ flexGrow: 1 }} />

      <TimezoneDisplayToggle
        mode={timezoneMode}
        onChange={onTimezoneModeChange}
        height={FILTER_FIELD_H}
      />

      {showRegister && (
        <CustomButton
          fullWidth={false}
          startIcon={<AddIcon />}
          onClick={onRegister}
        >
          Register
        </CustomButton>
      )}
    </Box>
  );
}
